import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsFetching } from 'react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Card,
  CardContent,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
  Button,
} from '@parrot-software/design-system';
import { useTranslation } from 'react-i18next';
import { formatCellValue } from '@reports/presentation/utils/dataFormatters';
import { getAlignmentClass } from '@reports/presentation/utils/columnConfigHelpers';
import { useReportTableBlock } from '@reports/presentation/hooks/useReportTableBlock';
import { useInfiniteTableBlock } from '@reports/presentation/hooks/useInfiniteTableBlock';
import { useIntersectionObserver } from '@reports/presentation/hooks/useIntersectionObserver';
import { useReportsState } from '@reports/presentation/contexts';
import { ReportBlockContainer } from '@reports/presentation/components/base/ReportBlockContainer';
import { ReportEmptyState } from '@reports/presentation/components/base/ReportEmptyState';
import { ReportErrorState } from '@reports/presentation/components/base/ReportErrorState';
import {
  TableBlockSkeleton,
  TableBlockPaginationSkeleton,
} from '@reports/presentation/components/base/TableBlockSkeleton';
import { ReportTableColumnToggle } from '@reports/presentation/components/base/ReportTableColumnToggle';
import type { TableBlockConfig } from '@reports/domain/value-objects/blocks/TableBlockConfig';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ColumnDefinition } from '@reports/domain/value-objects/blocks/ColumnConfig';

/**
 * Display column with key and definition
 */
interface DisplayColumn {
  key: string;
  definition: ColumnDefinition;
  originalIndex: number;
}

interface ReportTableBlockProps {
  blockConfig: TableBlockConfig;
  request: BlockDataRequestDto;
}

/**
 * ReportTableBlock - Dynamic table block component for reports
 *
 * Renders a table with configurable columns and formatted data based on
 * TableBlockConfig and TableBlockData. Supports multiple data formats
 * (TEXT, CURRENCY, NUMBER, DATE, etc.) with proper alignment and styling.
 *
 * Features:
 * - Dynamic column configuration from blockConfig (ReadonlyMap for O(1) lookups)
 * - Automatic data formatting based on column format type
 * - Loading, error, and empty state handling
 * - Responsive design with proper CSS variables from Parrot Toolkit
 * - Accessibility support with proper table semantics
 * - Column order preserved from Map insertion order
 *
 * @param {ReportTableBlockProps} props - Component properties
 * @param {TableBlockConfig} props.blockConfig - Table block configuration with columns Map and metadata
 * @param {BlockDataRequestDto} props.request - Request parameters (date range, filters)
 * @returns {React.ReactElement} Table block component with loading/error states
 *
 * @example
 * <ReportTableBlock
 *   blockConfig={salesSummaryTableConfig}
 *   request={{ startDate: new Date(), endDate: new Date(), filters: {} }}
 * />
 */
export const ReportTableBlock = ({ blockConfig, request }: ReportTableBlockProps) => {
  const { t } = useTranslation();
  const { state } = useReportsState();
  const isFiltersInitialized = state.isInitialized;
  const isFiltersLoading = useIsFetching(['reports', 'filter-catalog']) > 0;
  const isPaginationEnabled = blockConfig.paginationEnabled === true;

  const regularHook = useReportTableBlock({
    request,
    blockConfig,
    enabled: !isPaginationEnabled && isFiltersInitialized,
  });

  // Infinite scroll table hook
  const infiniteHook = useInfiniteTableBlock({
    request,
    blockConfig,
    enabled: isPaginationEnabled && isFiltersInitialized,
  });

  // Select the appropriate hook result based on pagination mode
  const { tableBlockData, tableBlockError, tableBlockState, refetchTableBlock } = isPaginationEnabled
    ? infiniteHook
    : regularHook;

  const canLoadMore =
    isPaginationEnabled && infiniteHook.hasNextPage && !infiniteHook.tableBlockState.isFetchingNextPage;

  const handleLoadMore = () => {
    if (canLoadMore) {
      infiniteHook.fetchNextPage();
    }
  };

  // Intersection observer for infinite scroll trigger
  // Placed after the last row, triggers fetchNextPage when visible
  const observerRef = useIntersectionObserver({
    onIntersect: handleLoadMore,
    enabled: isPaginationEnabled && !!infiniteHook.hasNextPage,
    threshold: 1.0,
  });

  /**
   * Cleanup effect: Remove infinite query cache on unmount
   * This ensures pagination always resets to page 1 when navigating between sections
   * Only applies to paginated tables to avoid unnecessary cache removal
   */
  useEffect(() => {
    return () => {
      if (isPaginationEnabled) {
        infiniteHook.removeQuery();
      }
    };
  }, [isPaginationEnabled, infiniteHook.removeQuery]);

  const showLoadingState =
    (!isFiltersInitialized || isFiltersLoading || tableBlockState.isFetching) &&
    !infiniteHook.tableBlockState.isFetchingNextPage;

  /**
   * State for column visibility (Set for O(1) lookup performance)
   * Initialize with all columns visible except the first one (always visible)
   */
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const allColumnKeys = Array.from(blockConfig.columnsConfig.keys());
    return new Set(allColumnKeys.slice(1)); // Skip first column (always visible)
  });

  /**
   * Toggle individual column visibility
   */
  const handleToggleColumn = useCallback((columnKey: string) => {
    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  }, []);

  /**
   * Toggle all columns at once (except first column)
   */
  const handleToggleAllColumns = useCallback(() => {
    const allColumnKeys = Array.from(blockConfig.columnsConfig.keys());
    const toggleableKeys = allColumnKeys.slice(1); // Skip first column

    setVisibleColumns((prev) => {
      const allVisible = toggleableKeys.every((key) => prev.has(key));
      return allVisible ? new Set() : new Set(toggleableKeys);
    });
  }, [blockConfig.columnsConfig]);

  /**
   * Prepare display columns based on visibility settings
   * Data is already filtered and aligned by the mapper (mapTableBlockDataToViewDto)
   * Filter by visibility: first column always visible, others based on visibleColumns Set
   * Index directly maps to row data index since mapper already aligned everything
   */
  const displayColumns = useMemo((): DisplayColumn[] => {
    if (!tableBlockData?.columns || tableBlockData.columns.length === 0) return [];

    return tableBlockData.columns
      .map((column, index) => {
        const definition = blockConfig.columnsConfig.get(column.key);

        if (!definition) {
          return null;
        }

        const isVisible = index === 0 || visibleColumns.has(column.key);
        if (!isVisible) {
          return null;
        }

        return { key: column.key, definition, originalIndex: index };
      })
      .filter((col): col is DisplayColumn => col !== null);
  }, [blockConfig.columnsConfig, tableBlockData?.columns, visibleColumns]);

  /**
   * Format cell value based on column configuration
   */
  const formatCell = useMemo(
    () => (value: string, columnKey: string): string => {
      const columnConfig = blockConfig.columnsConfig.get(columnKey);
      if (!columnConfig) {
        return value ?? '-';
      }
      return formatCellValue(value, columnConfig.format);
    },
    [blockConfig.columnsConfig],
  );

  /**
   * Virtualization setup for paginated tables
   * Only enabled when pagination is enabled to improve performance with many rows
   */
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowHeightRef = useRef<number>(50); // Track actual row height for better estimates

  const virtualizer = useVirtualizer({
    count: isPaginationEnabled && tableBlockData ? tableBlockData.rows.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => rowHeightRef.current, // Use tracked row height for better estimates
    overscan: 5, // Render 5 extra rows outside viewport for smooth scrolling
    enabled: isPaginationEnabled && !!tableBlockData,
  });

  if (showLoadingState) {
    return (
      <ReportBlockContainer>
        {blockConfig.columnToggleEnabled && (
          <div className="mb-3 flex justify-end">
            <Skeleton className="h-9 w-36 bg-(--base-accent-bg-accent50)" />
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <TableBlockSkeleton columnsConfig={blockConfig.columnsConfig} rows={7} />
          </CardContent>
        </Card>
      </ReportBlockContainer>
    );
  }

  if (tableBlockState.isError || tableBlockError) {
    return (
      <ReportBlockContainer>
        <Card>
          <CardContent className="h-[400px] flex items-center justify-center">
            <ReportErrorState onRetry={refetchTableBlock} />
          </CardContent>
        </Card>
      </ReportBlockContainer>
    );
  }

  if (!tableBlockData || tableBlockData.rows.length === 0) {
    return (
      <ReportBlockContainer>
        <Card>
          <CardContent className="h-[400px] flex items-center justify-center">
            <ReportEmptyState />
          </CardContent>
        </Card>
      </ReportBlockContainer>
    );
  }

  return (
    <ReportBlockContainer>
      {blockConfig.columnToggleEnabled && (
        <div className="mb-3 flex justify-end" data-testid="column-toggle-container">
          <ReportTableColumnToggle
            columnsConfig={blockConfig.columnsConfig}
            onToggleAllColumns={handleToggleAllColumns}
            onToggleColumn={handleToggleColumn}
            visibleColumns={visibleColumns}
          />
        </div>
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div
            className={cn('overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none', {
              'overflow-y-auto h-[calc(100vh-150px)] max-h-[calc(100vh-150px)] relative': isPaginationEnabled,
            })}
            data-testid="table-block-content"
            ref={isPaginationEnabled ? scrollContainerRef : undefined}
          >
            <Table className="w-full" wrapperClassName="overflow-visible">
              <TableHeader>
                <TableRow
                  className="bg-(--base-muted-muted) border-b border-(--base-border-border) sticky top-0 z-10 hover:bg-(--base-muted-muted)"
                  data-testid="table-header-row"
                >
                  {displayColumns.map(({ key, definition }) => (
                    <TableHead
                      className={cn(
                        'min-h-10 px-4 text-(--base-muted-muted-foreground) font-normal',
                        getAlignmentClass(definition.align),
                        'whitespace-nowrap',
                      )}
                      data-testid={`table-header-${key}`}
                      key={key}
                      style={{ width: definition.width }}
                    >
                      {t(definition.label)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody data-testid="table-body">
                {isPaginationEnabled ? (
                  <>
                    {/* Top padding to simulate rows above viewport */}
                    {virtualizer.getVirtualItems().length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={displayColumns.length}
                          style={{
                            height: `${virtualizer.getVirtualItems()[0]?.start ?? 0}px`,
                            padding: 0,
                            border: 0,
                          }}
                        />
                      </TableRow>
                    )}

                    {/* Virtualized rows for paginated tables - only render visible rows */}
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                      const row = tableBlockData.rows[virtualRow.index];
                      if (!row) return null;

                      return (
                        <TableRow
                          className="border-b border-(--base-border-border)"
                          data-testid={`table-row-${virtualRow.index}`}
                          key={virtualRow.key}
                        >
                          {displayColumns.map(({ key, definition, originalIndex }) => {
                            const cellValue = row[originalIndex] ?? '';
                            const formattedValue = formatCell(cellValue, key);

                            return (
                              <TableCell
                                className={cn(
                                  'px-4 py-2.5 text-foreground whitespace-nowrap',
                                  getAlignmentClass(definition.align),
                                  {
                                    'font-medium': definition.bold,
                                  },
                                )}
                                data-testid={`table-cell-${virtualRow.index}-${key}`}
                                key={`cell-${virtualRow.index}-${key}`}
                              >
                                {formattedValue}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}

                    {/* Bottom padding to simulate rows below viewport */}
                    {virtualizer.getVirtualItems().length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={displayColumns.length}
                          style={{
                            height: `${Math.max(
                              0,
                              virtualizer.getTotalSize() -
                                (virtualizer.getVirtualItems()[virtualizer.getVirtualItems().length - 1]?.end ?? 0),
                            )}px`,
                            padding: 0,
                            border: 0,
                          }}
                        />
                      </TableRow>
                    )}

                    {/* Infinite scroll trigger - placed after padding */}
                    {infiniteHook.hasNextPage && (
                      <TableRow>
                        <TableCell className="p-0 h-1" colSpan={displayColumns.length}>
                          <div className="h-1" data-testid="infinite-scroll-trigger" ref={observerRef} />
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Loading skeleton for next page fetch */}
                    {infiniteHook.tableBlockState.isFetchingNextPage && (
                      <TableBlockPaginationSkeleton displayColumns={displayColumns} />
                    )}
                  </>
                ) : (
                  <>
                    {/* Regular non-virtualized rows for non-paginated tables */}
                    {tableBlockData.rows.map((row, rowIndex) => (
                      <TableRow
                        className="border-b border-(--base-border-border)"
                        data-testid={`table-row-${rowIndex}`}
                        key={`row-${rowIndex}`}
                      >
                        {displayColumns.map(({ key, definition, originalIndex }) => {
                          const cellValue = row[originalIndex] ?? '';
                          const formattedValue = formatCell(cellValue, key);

                          return (
                            <TableCell
                              className={cn('px-4 py-2.5 text-foreground', getAlignmentClass(definition.align), {
                                'font-medium': definition.bold,
                              })}
                              data-testid={`table-cell-${rowIndex}-${key}`}
                              key={`cell-${rowIndex}-${key}`}
                            >
                              {formattedValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>

            {/* Manual "Load More" button - sticky centered in viewport during horizontal scroll */}
            {canLoadMore && (
              <div className="relative w-full py-2">
                <Button
                  className="sticky shadow-md left-1/2 -translate-x-1/2"
                  data-testid="load-more-button"
                  onClick={handleLoadMore}
                  size="sm"
                  variant="outline"
                >
                  {t('reportCommon:buttons.loadMore')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ReportBlockContainer>
  );
};
